  import React, { useState, useEffect, useCallback } from 'react';
  import api from '../apiReequest';
  import { FaEye, FaReply, FaEnvelopeOpenText, FaTrash } from 'react-icons/fa';
  import { Editor } from '@tinymce/tinymce-react';
  import SafeRichText from '../components/SafeRichText';
  import LoadingSpinner from '../components/LoadingSpinner';
  import { scrollToForm } from '../utils/adminFormUtils';
  import '../styles/adminCommonStyles.css';
  import DOMPurify from 'dompurify';

  export default function AdminContactMessages() {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState(null);
    const refetch = useCallback(async () => {
      setLoading(true);
      setFetchError(null);
      try {
        // Bypass cache for contact messages - always fetch fresh
        const res = await api.get('/contact', { 
          params: { _t: Date.now() }  // Cache buster parameter
        });
        console.log('[ADMIN CONTACT] Raw API response:', res.data);
        const { normalizeList } = await import('../utils/responseHelpers');
        // Normalize backend rows: convert is_read (0/1) to read (boolean)
        const raw = normalizeList(res) || [];
        console.log('[ADMIN CONTACT] Normalized list:', raw);
        const normalized = raw.map(m => ({
          ...m,
          // Convert is_read (0/1) to read (true/false). 0=unread, 1=read
          read: m.is_read ? (Number(m.is_read) === 1) : false
        }));
        // Sort by created_at in descending order (newest first)
        normalized.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        console.log('[ADMIN CONTACT] Final messages:', normalized);
        setMessages(normalized);
      } catch (err) {
        console.error('[ADMIN CONTACT] Error fetching messages:', err);
        setFetchError(err.response?.data?.message || err.message || 'Failed to fetch messages');
      } finally {
        setLoading(false);
      }
    }, []);

    useEffect(() => {
      refetch();
    }, [refetch]);
    const [error, setError] = useState('');
    const [replyModal, setReplyModal] = useState({ open: false, message: null });
    const [replySubject, setReplySubject] = useState("");
    const [replyBody, setReplyBody] = useState("");
    const [replyError, setReplyError] = useState("");
    const [replySuccess, setReplySuccess] = useState("");
    const [viewModal, setViewModal] = useState({ open: false, message: null });
    const [outlookChoiceModal, setOutlookChoiceModal] = useState({ open: false, to: '', subject: '', body: '' });

    // Calculate unread count whenever messages change
    const unreadCount = messages.filter(msg => {
      // Use the normalized `read` property which is always kept in sync
      return !Boolean(msg.read);
    }).length;

    // Update global unread count and log for debugging
    useEffect(() => {
      window.contactMessagesUnreadCount = unreadCount;
      console.log('Unread count updated:', unreadCount, 'Total messages:', messages.length);
      console.log('Messages:', messages);
    }, [unreadCount, messages]);

    // Check if superadmin
    let isSuperAdmin = false;
    try {
      const { authAPI } = require('../utils/authUtils');
      isSuperAdmin = authAPI && typeof authAPI.hasRole === 'function' ? authAPI.hasRole('superadmin') : false;
    } catch (e) {
      // Silent fail
    }

    // Handle logout
    const handleLogout = () => {
      localStorage.removeItem('token');
      window.location.href = '/admin/login';
    };

    // Mark message as read when viewing
    const handleViewMessage = async (msg) => {
      setViewModal({ open: true, message: msg });
      scrollToForm();
      if (!msg.read) {
        try {
          await api.post(`/contact/${msg.id}/read`);
          console.log('Message marked as read');
          // Directly update messages state
          setMessages(prevMessages =>
            prevMessages.map(m => m.id === msg.id ? { ...m, read: true, is_read: 1 } : m)
          );
          // Update modal message
          setViewModal(prev => ({
            ...prev,
            message: { ...prev.message, read: true }
          }));
        } catch (err) {
          console.error(
            'mark read failed:',
            err.response?.status,
            err.response?.data || err.message
          );
        }
      }
    };

    // Handle mark as unread
    const handleMarkUnread = async (msgId) => {
      try {
        await api.post(`/contact/${msgId}/unread`);
        console.log('Message marked as unread');
        // Directly update messages state
        setMessages(prevMessages =>
          prevMessages.map(m => m.id === msgId ? { ...m, read: false, is_read: 0 } : m)
        );
      } catch (err) {
        console.error(
          'mark unread failed:',
          err.response?.status,
          err.response?.data || err.message
        );
      }
    };


    // Handle reply via Outlook
    const handleReplySubmit = (e) => {
      e.preventDefault();
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = replyBody;
      const plainBody = tempDiv.textContent || tempDiv.innerText || '';
      setReplyModal({ open: false, message: null });
      setOutlookChoiceModal({
        open: true,
        to: replyModal.message.email,
        subject: replySubject,
        body: plainBody,
      });
    };

    const handleOpenOutlookApp = () => {
      const { to, subject, body } = outlookChoiceModal;
      const mailtoLink = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      const anchor = document.createElement('a');
      anchor.href = mailtoLink;
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      setOutlookChoiceModal({ open: false, to: '', subject: '', body: '' });
      setReplyModal({ open: false, message: null });
    };

    const handleOpenOutlookWeb = () => {
      const { to, subject, body } = outlookChoiceModal;
      window.open(
        `https://outlook.live.com/mail/deeplink/compose?to=${encodeURIComponent(to)}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,
        '_blank'
      );
      setOutlookChoiceModal({ open: false, to: '', subject: '', body: '' });
      setReplyModal({ open: false, message: null });
    };

    // Handle delete message
    const handleDeleteMessage = async (msgId) => {
      if (!window.confirm('Are you sure you want to delete this message? This action cannot be undone.')) return;
      try {
        await api.delete(`/contact/${msgId}`);
        await refetch();
      } catch (err) {
        setError(err.response?.data?.error || err.message || 'Failed to delete message');
      }
    };

    // Handle opening reply modal
    const handleOpenReply = (msg) => {
      // Strip HTML tags from message for cleaner plain text reply
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = msg.message;
      const plainTextMessage = tempDiv.textContent || tempDiv.innerText || '';
      setReplyModal({ open: true, message: msg });
      setReplySubject(`Re: ${msg.subject || 'Contact Form Message'}`);
      setReplyBody(`Hi ${msg.name},\n\nThank you for contacting us. Regarding your message:\n\n"${plainTextMessage.trim()}"\n\n---\nYour reply here.`);
      setReplyError("");
      setReplySuccess("");
    };

    return (
      <div className="p-2 sm:p-4 md:p-8 w-full max-w-screen-2xl mx-auto flex flex-col items-center justify-center">
        <div className="w-full flex flex-col items-start mb-2">
          <h2 className="text-3xl md:text-4xl font-bold text-black text-center sm:text-left">
            Contact Messages
          </h2>
          {unreadCount > 0 && (
            <span className="mt-2 px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
              {unreadCount} unread
            </span>
          )}
        </div>
        <div className="w-full flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
          <div className="flex items-center gap-2 sm:gap-4 justify-center sm:justify-end">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${isSuperAdmin ? 'bg-green-100 text-green-800 border border-green-300' : 'bg-blue-100 text-blue-800 border border-blue-300'}`}>
              {isSuperAdmin ? 'Super Admin' : 'Admin'}
            </span>
            <button
              onClick={handleLogout}
              className="admin-btn-danger"
            >
              Logout
            </button>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <LoadingSpinner message="Loading messages..." bgColor="bg-white" spinnerColor="border-[#73ab40]" py={12} />
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-100 border border-red-300 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Messages Table */}
        {!loading && !error && (
          <div className="admin-table-container">
            <div className="w-full overflow-x-auto">
              <table className="admin-table">
              {/* Table is wrapped for scroll on desktop if needed */}
              <thead className="admin-table-head">
                <tr>
                  <th className="admin-table-header-cell">Name</th>
                  <th className="admin-table-header-cell">Email</th>
                  <th className="admin-table-header-cell">Subject</th>
                  <th className="admin-table-header-cell">Date</th>
                  <th className="admin-table-header-cell">Actions</th>
                </tr>
              </thead>
              <tbody className="admin-table-body-row">
                {messages.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="py-8 px-4 text-center text-gray-500">
                      <div className="text-lg">No messages found</div>
                      <div className="text-sm mt-1">Contact messages will appear here when received.</div>
                    </td>
                  </tr>
                ) : (
                  messages.map((msg) => (
                    <tr key={msg.id} className={`hover:bg-blue-50 transition-colors ${!msg.read ? 'bg-blue-25' : ''}`}>
                      <td className="py-2 px-2 font-medium text-gray-900 text-sm">
                        <div className="flex items-center gap-2">
                          {!msg.read && (
                            <span className="w-2.5 h-2.5 bg-red-500 rounded-full flex-shrink-0"></span>
                          )}
                          {msg.name && msg.name.length > 50 ? msg.name.slice(0, 50) + '…' : msg.name}
                        </div>
                      </td>
                      <td className="py-2 px-2 font-medium text-gray-900 text-sm">
                        {msg.email && msg.email.length > 50 ? msg.email.slice(0, 50) + '…' : msg.email}
                      </td>
                      <td className="py-2 px-2 max-w-xs truncate text-sm" title={msg.subject}>
                          {msg.subject && msg.subject.length > 20 ? msg.subject.slice(0, 20) + '…' : msg.subject}
                      </td>
                      <td className="py-2 px-2 font-medium text-gray-900 whitespace-nowrap text-sm">
                        {new Date(msg.created_at).toLocaleDateString('en-GB')}
                      </td>
                      <td className="py-2 px-3 max-w-[280px]">
                        <div className="grid grid-cols-2 gap-2 items-stretch justify-center">
                          <button
                            onClick={() => handleViewMessage(msg)}
                            className="admin-btn-icon-view"
                          >
                            <FaEye className="text-base" /> View
                          </button>
                          <button
                            onClick={() => handleOpenReply(msg)}
                            className="admin-btn-icon-reply"
                          >
                            <FaReply className="text-base" /> Reply
                          </button>
                          <button
                            onClick={() => handleMarkUnread(msg.id)}
                            className="admin-btn-icon-warning"
                          >
                            <FaEnvelopeOpenText className="text-base" style={{ minWidth: '0.9rem', minHeight: '0.9rem' }} /> Mark Unread
                          </button>
                          <button
                            onClick={() => handleDeleteMessage(msg.id)}
                            className="admin-btn-icon-delete"
                          >
                            <FaTrash className="text-base" /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
        </div>
          </div>
        )}

        {/* View Modal */}
        {viewModal.open && (
          <div className="admin-modal-overlay">
            <div className="admin-modal-content admin-modal-form" id="view-message-form">
              <div className="flex items-center gap-2 mb-4">
                <FaEye className="text-blue-600 text-xl" />
                <h3 className="text-xl font-bold text-blue-700">Message Details</h3>
              </div>
              <div className="space-y-3">
                <div>
                  <strong className="text-gray-700">Name:</strong>
                  <div className="text-gray-900">{viewModal.message.name}</div>
                </div>
                <div>
                  <strong className="text-gray-700">Email:</strong>
                  <div className="text-gray-900">{viewModal.message.email}</div>
                </div>
                <div>
                  <strong className="text-gray-700">Subject:</strong>
                  <div className="text-gray-900">{viewModal.message.subject}</div>
                </div>
                <div>
                  <strong className="text-gray-700">Date:</strong>
                  <div className="text-gray-900">{new Date(viewModal.message.created_at).toLocaleString()}</div>
                </div>
                <div>
                  <strong className="text-gray-700">Message:</strong>
                  <div className="mt-2 p-3 bg-blue-50 rounded-lg text-gray-800 whitespace-pre-line">
                    <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(viewModal.message.message) }} />
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setViewModal({ open: false, message: null })}
                  className="admin-btn-secondary"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setViewModal({ open: false, message: null });
                    handleOpenReply(viewModal.message);
                  }}
                  className="admin-btn-primary"
                >
                  Reply
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reply Modal */}
        {replyModal.open && (
          <div className="admin-modal-overlay">
            <div className="admin-modal-content admin-modal-form" id="reply-message-form">
              <h3 className="text-xl font-bold mb-4 text-blue-700">
                Reply to {replyModal.message.name}
              </h3>
              <form onSubmit={handleReplySubmit} className="space-y-4">
                <div>
                  <label className="admin-label">To</label>
                  <input
                    type="email"
                    value={replyModal.message.email}
                    disabled
                    className="admin-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                  <input
                    type="text"
                    value={replySubject}
                    onChange={(e) => setReplySubject(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                  <Editor
                    value={replyBody}
                    onEditorChange={(val) => setReplyBody(val)}
                    init={{
                      apiKey: process.env.REACT_APP_TINYMCE_API_KEY,
                      height: 300,
                      menubar: false,
                      branding: false,
                      analytics: false,
                      external_plugins: {},
                      plugins: 'advlist autolink lists link image charmap anchor searchreplace visualblocks code fullscreen insertdatetime media table help wordcount',
                      toolbar:
                        'undo redo | formatselect | bold italic backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | help'
                    }}
                  />
                </div>
                {replyError && (
                  <div className="p-3 bg-red-100 border border-red-300 text-red-700 rounded">
                    {replyError}
                  </div>
                )}
                {replySuccess && (
                  <div className="p-3 bg-green-100 border border-green-300 text-green-700 rounded">
                    {replySuccess}
                  </div>
                )}
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    className="admin-btn-secondary"
                    onClick={() => setReplyModal({ open: false, message: null })}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="admin-btn-primary"
                    onClick={() => {
                      const htmlContent = replyModal.message.message;
                      const blob = new Blob([htmlContent], { type: 'text/html' });
                      const data = [new ClipboardItem({ 'text/html': blob })];
                      navigator.clipboard.write(data).then(() => {
                        setReplySuccess('Formatted message copied to clipboard!');
                        setTimeout(() => setReplySuccess(''), 2000);
                      }).catch(err => {
                        setReplyError('Failed to copy formatted message');
                        setTimeout(() => setReplyError(''), 2000);
                      });
                    }}
                  >
                    Copy Format
                  </button>
                  <button
                    type="submit"
                    className="admin-btn-primary"
                  >
                    Send via Outlook
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Outlook Choice Modal */}
        {outlookChoiceModal.open && (
          <div className="admin-modal-overlay" style={{ zIndex: 60 }}>
            <div className="admin-modal-content" style={{ maxWidth: '420px', textAlign: 'center', padding: '2rem' }}>
              <div className="flex flex-col items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <FaReply className="text-blue-600 text-xl" />
                </div>
                <h3 className="text-xl font-bold text-gray-800">Open reply in...</h3>
                <p className="text-sm text-gray-500">
                  Choose how you'd like to open this reply. If Outlook is installed on your device, use the desktop app option.
                </p>
              </div>
              <div className="flex flex-col gap-3 mb-4">
                <button
                  onClick={handleOpenOutlookApp}
                  className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
                >
                  <FaEnvelopeOpenText className="text-base" />
                  Outlook Desktop App
                </button>
                <button
                  onClick={handleOpenOutlookWeb}
                  className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-white hover:bg-gray-50 text-blue-600 border-2 border-blue-600 rounded-lg font-semibold transition-colors"
                >
                  <FaReply className="text-base" />
                  Outlook Web (Browser)
                </button>
              </div>
              <p className="text-xs text-gray-400 mb-4">
                The desktop app option opens your system's default mail client. If it opens Gmail or another browser app instead, you need to clear your browser's mail protocol handler: go to <strong>Browser Settings → Privacy → Handlers</strong> and remove the mailto handler, then set Outlook as your default mail app in your OS settings.
              </p>
              <button
                onClick={() => setOutlookChoiceModal({ open: false, to: '', subject: '', body: '' })}
                className="admin-btn-secondary w-full"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  export { AdminContactMessages };